import React, { useEffect } from 'react';
import { useForm, SubmitHandler, FieldValues } from 'react-hook-form';
import {
  useApiClient,
  useFetch,
} from '@marageti/z4-lib';
import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';
import { Agent } from '@marageti/z4-sdk/lib/people';
import { User } from '@marageti/z4-sdk/lib/auth';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import EditAgentFormView from './edit-agent-form-view';
import { ContainerProps } from './types';
import { validatePhone } from '../../../utils/helpers/validation';

const EditAgentFormContainer = ({
  open,
  onClose,
}: ContainerProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const { agentId } = useParams<{ agentId: string }>();
  const [submissionError, setSubmissionError] = React.useState<ApiError>();

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
    reset,
    setError,
  } = useForm({
    mode: 'onChange',
  });

  const apiClient = useApiClient();

  const { data: agent, mutate: mutateAgent } = useFetch<Agent>(apiClient.peopleClient.getAgent, [agentId], !!agentId);
  const { data: user, mutate: mutateUser } = useFetch<User>(apiClient.authClient.getUser, [agent?.userInfo[0].id], true);

  const defaultSelectedRoles = agent?.userInfo[0]?.roles?.[0];

  const handleCancel = () => {
    reset();
    onClose();
  };

  const onSubmit: SubmitHandler<FieldValues> = async (data) => {
    if (agent && user) {
      const updatedAgent = {
        ...agent,
        givenName: data.givenName,
        familyName: data.familyName,
        title: data.title,
        phone: {
          number: data.phone?.number,
          prefix: data.phone?.prefix,
        },
        timeZone: data.timeZone,
      };

      const updatedUser = {
        ...user,
        roles: [data.role],
      };

      try {
        await apiClient.peopleClient.putAgent(agent.id, updatedAgent);
        await apiClient.authClient.putUser(user.id, updatedUser);
        mutateUser();
        mutateAgent();
        enqueueSnackbar('User updated.', { variant: 'success' });
        onClose();
      } catch (error) {
        setSubmissionError(error as ApiError);
        enqueueSnackbar('An error occurred during the update.', { variant: 'default' });
      }
    }
  };

  useEffect(() => {
    if (agent?.phone) {
      const validationResult = validatePhone(agent.phone);
      if (validationResult !== true) {
        setError('phone', { type: 'manual', message: 'error' });
      }
    }
  }, [agent, setError]);

  return (
    <EditAgentFormView
      control={control}
      defaultSelectedRoles={defaultSelectedRoles}
      errors={errors}
      handleSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      isValid={isValid}
      onCancel={handleCancel}
      onSubmit={onSubmit}
      open={open}
      register={register}
      submissionError={submissionError}
      agent={agent}
    />
  );
};

export default EditAgentFormContainer;
